import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { addError } from '../store/Error';

export type CallResult<T> =
  | {
      data: T;
      error: null;
    }
  | {
      data: null;
      error: {
        message: string;
        errorResponse?: AxiosError;
      };
    };

export type ApiUrl = string;
export type CallExternalApiOptions = Omit<AxiosRequestConfig, 'url'> & {
  url: ApiUrl;
};

export function isTokenExpired(token: string | null): boolean {
  if (!token) return true;
  try {
    const base64url = token.split('.')[1];
    // Normalize base64url → standard base64 (replace chars, restore padding)
    const base64 = base64url
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(base64url.length / 4) * 4, '=');
    const payload = JSON.parse(atob(base64));
    return typeof payload.exp !== 'number' || payload.exp * 1000 <= Date.now();
  } catch {
    return true;
  }
}

// Browsers refuse a keepalive fetch once the bodies of those in flight pass 64 KiB; this leaves room for headers.
const KEEPALIVE_BODY_BUDGET = 60 * 1024;
let keepAliveBudget = 0;

/**
 * Sends the requests `send` starts with fetch keepalive, so the browser finishes them after the tab closes.
 * Only requests started synchronously inside `send` are kept alive, while their bodies fit the browser's
 * shared keepalive limit; a larger one goes out as an ordinary request.
 */
export const sendKeptAlive = (send: () => void) => {
  keepAliveBudget = KEEPALIVE_BODY_BUDGET;
  try {
    send();
  } finally {
    keepAliveBudget = 0;
  }
};

const fitsKeepAlive = (data: unknown): boolean => {
  if (keepAliveBudget === 0) return false;
  const size = new Blob([data === undefined ? '' : JSON.stringify(data)]).size;
  if (size > keepAliveBudget) return false;
  keepAliveBudget -= size;
  return true;
};

export async function callExternalApi<T>(config: CallExternalApiOptions, skipAuth = false): Promise<CallResult<T>> {
  const keptAlive = fitsKeepAlive(config.data);
  const token = localStorage.getItem('token');
  if (!token && !skipAuth) {
    return { data: null, error: { message: 'Not authenticated or missing token' } };
  }

  try {
    const { data } = await axios({
      ...config,
      ...(keptAlive ? { adapter: 'fetch' as const, fetchOptions: { keepalive: true } } : {}),
      withCredentials: true,
      headers: {
        ...config.headers,
        ...(skipAuth ? {} : { Authorization: `Bearer ${token}` })
      },
      baseURL: `${process.env.REACT_APP_API_SERVER_URL}/`
    });

    return { data, error: null };
  } catch (error) {
    const axiosError = error as AxiosError;
    if (!skipAuth && axiosError?.response?.status === 401 && isTokenExpired(token)) {
      addError('session', axiosError.message);
    }
    // Either config hands a serializer the bearer token, body and query params; both are the same object.
    delete axiosError?.config;
    if (axiosError?.response) delete (axiosError.response as Partial<AxiosResponse>).config;
    return {
      data: null,
      error: {
        message: (error as Error).message,
        errorResponse: axiosError
      }
    };
  }
}
